/**
 * InlineCountdown - Ticking inline countdown timer for template text
 *
 * Modes:
 * - duration: counts down from N seconds since component mount
 * - targetDate: counts down to an ISO datetime (recalculates from Date.now() each tick to avoid drift)
 *
 * Stops at 0 and displays "00:00" (or equivalent in the configured format).
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { InlineCountdownConfig, InlineCountdownFormat } from '@segmently/schema-models/v2';

export interface InlineCountdownProps {
  config: InlineCountdownConfig;
  'data-testid'?: string;
}

/**
 * Format remaining seconds into the specified display format
 */
function formatTime(totalSeconds: number, format: InlineCountdownFormat): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));

  if (format === 'DD:HH:MM:SS') {
    const days = Math.floor(safeSeconds / 86400);
    const hours = Math.floor((safeSeconds % 86400) / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    return `${String(days).padStart(2, '0')}:${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  if (format === 'HH:MM:SS') {
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  // Default: MM:SS
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Calculate initial remaining seconds from config
 */
function getInitialRemaining(config: InlineCountdownConfig): number {
  if (config.mode === 'duration') {
    return config.duration ?? 0;
  }

  // targetDate mode: calculate diff from now
  if (config.targetDate) {
    const target = new Date(config.targetDate as unknown as string).getTime();
    const diff = Math.floor((target - Date.now()) / 1000);
    return Math.max(0, diff);
  }

  return 0;
}

export function InlineCountdown({
  config,
  'data-testid': testId,
}: InlineCountdownProps): React.ReactElement {
  const [remaining, setRemaining] = useState(() => getInitialRemaining(config));

  const tick = useCallback(() => {
    if (config.mode === 'targetDate' && config.targetDate) {
      // Recalculate from Date.now() each tick to avoid drift
      const target = new Date(config.targetDate as unknown as string).getTime();
      const diff = Math.floor((target - Date.now()) / 1000);
      setRemaining(Math.max(0, diff));
    } else {
      setRemaining((prev) => Math.max(0, prev - 1));
    }
  }, [config]);

  useEffect(() => {
    if (remaining <= 0) return;

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [remaining, tick]);

  return (
    <span
      data-testid={testId || 'inline-countdown'}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {formatTime(remaining, config.format)}
    </span>
  );
}

// Export formatTime for testing
export { formatTime };

export default InlineCountdown;
